import React, { useState, useEffect, useCallback } from 'react';
import { useBackRoute } from '../hooks/useBackRoute';
import { toValidObjectIdString, formatApiDate } from '../utils/security';
import { fetchCihazDegisimleri, fetchCihazDegisimiDetay, fetchCihazDegisimiSiparisi } from '../services/cihazDegisimiService';
import CihazDegisimiDetayModal from '../components/CihazDegisimiDetayModal';
import CihazDegisimiOlusturModal from '../components/CihazDegisimiOlusturModal';
import TableListFooter from '../components/TableListFooter';
import { PAGE_DEFAULT_LIMIT } from '../constants/pagination';
import { normalizeSayfalama } from '../utils/pagination';

const TABS = [
  { id: 'onay-bekleyen', label: 'Onay Bekleyen', tab: 'onay-bekleyen' },
  { id: 'onaylanan', label: 'Onaylanan', tab: 'onaylanan' },
];

const getCihazDegisimiId = (c) => toValidObjectIdString(c?.id ?? c?._id) || '';

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  const msg = error?.message || error?.data?.mesaj || error?.data?.message || fallback;
  return msg === 'Failed to fetch' ? 'Sunucuya bağlanılamadı' : msg;
};

const CihazDegisimiPage = () => {
  const goBack = useBackRoute('/');
  const [cihazDegisimleri, setCihazDegisimleri] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('onay-bekleyen');
  const [selectedDetay, setSelectedDetay] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [error, setError] = useState('');
  const [kargoModalOpen, setKargoModalOpen] = useState(false);
  const [kargoSiparis, setKargoSiparis] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationMeta, setPaginationMeta] = useState({ total: 0, totalPages: 1 });

  const loadCihazDegisimleri = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const tab = activeTab === 'onaylanan' ? 'onaylanan' : 'onay-bekleyen';
      const response = await fetchCihazDegisimleri({ tab, sayfa: currentPage, limit: PAGE_DEFAULT_LIMIT });
      if (response?.basarili && response?.veri) {
        setCihazDegisimleri(response.veri.cihazDegisimleri || []);
        setPaginationMeta(normalizeSayfalama(response.veri.sayfalama));
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Cihaz değişimleri yüklenemedi'));
      setCihazDegisimleri([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab, currentPage]);

  useEffect(() => {
    loadCihazDegisimleri();
  }, [loadCihazDegisimleri]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setCurrentPage(1);
  };

  const handleViewDetail = async (item) => {
    const id = getCihazDegisimiId(item);
    if (!id) {
      window.showNotification?.('error', 'Cihaz değişimi ID alınamadı');
      return;
    }
    try {
      const response = await fetchCihazDegisimiDetay(id);
      if (response?.basarili && response?.veri) {
        setSelectedDetay(response.veri);
        setDetailModalOpen(true);
      }
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Detay yüklenemedi'));
    }
  };

  const handleCreateSuccess = () => {
    setCreateModalOpen(false);
    loadCihazDegisimleri();
  };

  const handleViewKargo = async (item) => {
    const id = getCihazDegisimiId(item);
    if (!id) return;
    try {
      const response = await fetchCihazDegisimiSiparisi(id);
      if (response?.basarili && response?.veri) {
        setKargoSiparis({ ...response.veri, cihazDegisimiNumarasi: item.cihazDegisimiNumarasi, urunAdi: item.urunAdi, seriNumarasi: item.seriNumarasi });
        setKargoModalOpen(true);
      } else {
        window.showNotification?.('info', 'Bu cihaz değişimine ait sipariş henüz oluşmamış');
      }
    } catch (err) {
      window.showNotification?.('error', 'Sipariş bilgisi yüklenemedi');
    }
  };

  return (
    <main className="main-content cihaz-degisimi-tech">
      <div className="content-header">
        <div>
          <button
            type="button"
            className="secondary-btn"
            onClick={goBack}
            style={{ marginBottom: '16px' }}
          >
            <i className="fas fa-arrow-left"></i>
            <span>Geri Dön</span>
          </button>
          <h1>Cihaz Değişimi</h1>
        </div>
        <button
          type="button"
          className="btn-add-new"
          onClick={() => setCreateModalOpen(true)}
        >
          <i className="fas fa-plus"></i>
          Cihaz Değişimi Oluştur
        </button>
      </div>

      <div className="tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => handleTabChange(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="card">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <div className="loading-text">Cihaz değişimleri yükleniyor</div>
          </div>
        ) : error ? (
          <div className="error-state">{error}</div>
        ) : cihazDegisimleri.length === 0 ? (
          <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
            <i className="fas fa-exchange-alt" style={{ fontSize: '32px', color: 'var(--text-light)', marginBottom: '12px' }}></i>
            <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>Cihaz değişimi talebi bulunamadı</div>
          </div>
        ) : (
          <>
          <div className="table-container">
            <table className="admins-table">
              <thead>
                <tr>
                  <th>Cihaz Değişim No</th>
                  <th>Randevu No</th>
                  <th>Cihaz (Seri No / Ürün)</th>
                  <th>Tarih</th>
                  <th>Durum</th>
                  <th className="islemler-sutunu">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {cihazDegisimleri.map((c, idx) => (
                  <tr key={getCihazDegisimiId(c) || idx}>
                    <td style={{ fontWeight: 500 }}>{c.cihazDegisimiNumarasi}</td>
                    <td>{c.randevuNumarasi || '—'}</td>
                    <td>{c.seriNumarasi ? `${c.seriNumarasi}${c.urunAdi ? ` / ${c.urunAdi}` : ''}` : c.urunAdi || '—'}</td>
                    <td>{formatApiDate(c.olusturmaTarihi)}</td>
                    <td>
                      <span className={`status-badge ${
                        c.durum === 'OnayBekliyor' ? 'status-bekliyor' :
                        c.durum === 'Onaylandi' ? 'status-tamamlandi' :
                        c.durum === 'IptalEdildi' ? 'status-iptal' : ''
                      }`}>
                        {c.durum === 'OnayBekliyor' ? 'Onay Bekliyor' :
                         c.durum === 'Onaylandi' ? 'Onaylandı' :
                         c.durum === 'IptalEdildi' ? 'İptal Edildi' : c.durum}
                      </span>
                    </td>
                    <td className="table-actions islemler-sutunu" style={{ display: 'flex', gap: '6px' }}>
                      <button
                        type="button"
                        className="category-action-btn edit-btn"
                        onClick={() => handleViewDetail(c)}
                        title="Detay"
                      >
                        <i className="fas fa-eye"></i>
                      </button>
                      {c.durum === 'Onaylandi' && (
                        <button
                          type="button"
                          className="category-action-btn edit-btn"
                          onClick={() => handleViewKargo(c)}
                          title="Kargo Bilgileri"
                          style={{ background: 'var(--primary-color)', color: '#fff' }}
                        >
                          <i className="fas fa-truck"></i>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <TableListFooter
            totalRecords={paginationMeta.total}
            currentPage={currentPage}
            totalPages={paginationMeta.totalPages || 1}
            onPageChange={setCurrentPage}
            loading={loading}
            embedded
          />
          </>
        )}
      </div>

      {detailModalOpen && selectedDetay && (
        <CihazDegisimiDetayModal
          cihazDegisimi={selectedDetay}
          onClose={() => {
            setDetailModalOpen(false);
            setSelectedDetay(null);
          }}
          onRefresh={loadCihazDegisimleri}
          isAdmin={false}
        />
      )}

      {createModalOpen && (
        <CihazDegisimiOlusturModal
          onClose={() => setCreateModalOpen(false)}
          onSuccess={handleCreateSuccess}
        />
      )}

      {kargoModalOpen && kargoSiparis && (
        <div className="modal-overlay" onClick={() => setKargoModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>
                <i className="fas fa-truck" style={{ marginRight: '8px' }}></i>
                Kargo Bilgileri
              </h2>
              <button type="button" className="modal-close-btn" onClick={() => setKargoModalOpen(false)} aria-label="Kapat">
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--text-gray)', marginBottom: '4px' }}>Cihaz Değişim No</div>
                  <div style={{ fontWeight: 600 }}>{kargoSiparis.cihazDegisimiNumarasi || '—'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--text-gray)', marginBottom: '4px' }}>Sipariş No</div>
                  <div style={{ fontWeight: 600 }}>{kargoSiparis.siparisNumarasi || '—'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--text-gray)', marginBottom: '4px' }}>Ürün</div>
                  <div>{kargoSiparis.urunAdi || '—'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--text-gray)', marginBottom: '4px' }}>Seri No</div>
                  <div>{kargoSiparis.seriNumarasi || '—'}</div>
                </div>
              </div>
              <div style={{ padding: '16px', borderRadius: '8px', background: 'var(--bg-light, #f8f9fa)', marginBottom: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '12px' }}>Sipariş Durumu</div>
                <span className={`status-badge ${
                  kargoSiparis.durum === 'KargoBekliyor' ? 'status-bekliyor' :
                  kargoSiparis.durum === 'Kargoda' ? 'status-aktif' :
                  kargoSiparis.durum === 'Tamamlandi' ? 'status-tamamlandi' : ''
                }`}>
                  {kargoSiparis.durum === 'KargoBekliyor' ? 'Kargo Bekleniyor' :
                   kargoSiparis.durum === 'Kargoda' ? 'Kargoda' :
                   kargoSiparis.durum === 'Tamamlandi' ? 'Tamamlandı' : kargoSiparis.durum}
                </span>
                {kargoSiparis.kargoFirmasi && (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-gray)', marginBottom: '4px' }}>Kargo Firması</div>
                    <div>{kargoSiparis.kargoFirmasi}</div>
                  </div>
                )}
                {kargoSiparis.kargoTakipNo && (
                  <div style={{ marginTop: '8px' }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-gray)', marginBottom: '4px' }}>Takip No</div>
                    <div>{kargoSiparis.kargoTakipNo}</div>
                  </div>
                )}
                {kargoSiparis.kargoTarihi && (
                  <div style={{ marginTop: '8px' }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-gray)', marginBottom: '4px' }}>Kargo Tarihi</div>
                    <div>{formatApiDate(kargoSiparis.kargoTarihi)}</div>
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setKargoModalOpen(false)}>Kapat</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default CihazDegisimiPage;
